import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { payoffShape, type PayoffLeg, type PayoffShape } from '@apna/shared'
import { cn } from '@/lib/utils'
import { compactInr } from '@/screens/terminal/quantity'
import { payoffPlot, scaleX, scrubAt, type PayoffModel, type PayoffScrub } from './payoffView'

/**
 * The payoff of a basket, at expiry, in rupees — ROADMAP 6.8.
 *
 * Two callers: an OPEN position on the portfolio screen, and the basket a
 * trader is about to place in `StrategyPicker`. Both pass the same thing —
 * signed quantities and the price each leg was taken at — so the curve shown
 * before entry and the curve shown after it are the same drawing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOT THE STRATEGY GLYPH, AND NOT A GUESS
 *
 * `terminal/payoff.ts` draws a 40x20 icon in strike-offset space with premium
 * ignored — a shape, for choosing from a list. This is the same position in
 * rupees, at its real strikes and real entry prices, at expiry.
 *
 * AT EXPIRY, AND IT SAYS SO.
 *
 * The expiry curve is exact. A caller may also supply a T+0 valuation
 * solved from that expiry's forward and per-leg IVs. This component never
 * guesses those inputs or substitutes cash spot for the forward.
 *
 * AN UNBOUNDED TAIL IS A WORD, NOT A NUMBER.
 *
 * `payoffShape` reports a naked short's loss as UNBOUNDED because spot has
 * no ceiling. The label says so. Printing the loss at the right edge of the
 * window instead would be a figure that changes when the window does, which
 * is the kind of number someone sizes a position against.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const PAD = { l: 68, r: 26, t: 30, b: 34 }

/** A P&L with its sign, which is the information — "₹1.2L" alone is not. */
const signedInr = (v: number): string =>
  `${v < 0 ? '-₹' : '+₹'}${compactInr(Math.abs(v))}`

export interface PayoffChartProps {
  legs: readonly PayoffLeg[]
  /** Live underlying price, for the marker. Null when unknown. */
  spot: number | null
  /** Value TODAY at a hypothetical spot. Null when no forward is available. */
  t0?: ((spot: number) => number) | null
  height?: number
  className?: string
  /** Hide the footer when the caller shows the same figures itself. */
  compact?: boolean
  /** An explicit spot span, for a caller that owns a range control. */
  window?: { from: number; to: number }
  /**
   * A valuation to draw INSTEAD of settlement — ROADMAP 9.67.
   *
   * For a basket whose legs do not expire together there is no single
   * at-expiry curve, so the caller solves one on the nearest expiry and
   * hands it in with the shape that goes with it. Both travel together so
   * the footer cannot describe a different curve from the one drawn.
   */
  model?: { valuation: PayoffModel; shape: PayoffShape }
}

export function PayoffChart({
  legs,
  spot,
  t0 = null,
  height = 220,
  className,
  compact = false,
  window: span,
  model,
}: PayoffChartProps) {
  const gid = useId().replace(/:/g, '')
  const plot = useMemo(
    () => payoffPlot(legs, spot, 160, span, model?.valuation),
    [legs, spot, span, model],
  )
  // The model's own, when there is one: a footer derived from the legs would
  // describe settlement while the curve above it describes the horizon.
  const legShape = useMemo(() => payoffShape(legs), [legs])
  const shape = model?.shape ?? legShape
  const container = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(640)
  useEffect(() => {
    const el = container.current
    if (!el) return
    const measure = () => { if (el.clientWidth > 0) setWidth(Math.round(el.clientWidth)) }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const todayValues = useMemo(() => t0 ? plot.points.map((p) => t0(p.spot)) : null, [plot, t0])

  /*
   * ── THE CROSSHAIR — ROADMAP 9.47 ────────────────────────────────────────
   *
   * "What is this position worth if NIFTY is at 23,600" is the question a
   * payoff chart is opened to answer, and until now the only spot it could
   * answer for was the live one. The curve already holds the answer at 160
   * sampled spots; this is the readout.
   *
   * POINTER EVENTS, not mouse: one handler covers a trackpad, a stylus and
   * a finger drag, and `pointerleave` fires for all three. The SVG takes
   * them on an overlay rect rather than on the paths, because a 2.2px line
   * is not a hit target.
   *
   * Held in component state and NOT in a ref with a manual DOM write. The
   * crosshair is at most one re-render per pointer move of a component that
   * is one <svg> — the chain's 12 Hz tick rule (§4.7) is about hundreds of
   * rows, and borrowing its machinery here would be cost without a reason.
   */
  /*
   * WHAT IS STORED IS THE POINTER'S COLUMN, NOT THE READING.
   *
   * Storing the reading and clearing it whenever the data changed made the
   * crosshair flash out and back on every tick — `plot` depends on `spot`,
   * so a live underlying invalidated it several times a second and it only
   * came back on the next pointer move.
   *
   * A pixel column is the thing the reader actually chose; the reading is a
   * function of it and the current curve. Deriving it each render means the
   * line stays exactly under the cursor while the numbers beside it update
   * with the feed, which is what a live chart should do. It also fixes the
   * second-order version of the same bug: in a fixed-span window the frame
   * re-centres on spot each tick, so a stored SPOT would drift out from
   * under the pointer even if nothing cleared it.
   */
  const [scrubX, setScrubX] = useState<number | null>(null)

  if (legs.length === 0) {
    return (
      <div
        ref={container}
        className={cn('flex flex-col items-center justify-center text-muted-foreground', className)}
        style={{ minHeight: height }}
        data-testid="payoff-empty"
      >
        <span className="text-[12px] font-semibold">Nothing open</span>
        <span className="mt-1 text-[10px]">A payoff needs at least one leg.</span>
      </div>
    )
  }

  // SVG units are CSS pixels. A fixed 640-wide viewBox stretched text and
  // circles threefold on a desktop dock; measuring width fixes every label.
  const W = width
  let lo = plot.lo
  let hi = plot.hi
  for (const value of todayValues ?? []) {
    if (!Number.isFinite(value)) continue
    lo = Math.min(lo, value)
    hi = Math.max(hi, value)
  }
  const x = (s: number): number => scaleX(s, plot.window, W, PAD.l, PAD.r)
  const y = (v: number): number => height - PAD.b - ((v - lo) / (hi - lo || 1)) * Math.max(1, height - PAD.t - PAD.b)
  const zeroY = y(0)

  const path = plot.points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.spot).toFixed(1)},${y(p.value).toFixed(1)}`)
    .join(' ')

  /*
   * The T+0 line is sampled on the SAME x positions as the expiry curve, so
   * the two are directly comparable at every point a reader might measure —
   * including the kinks, where the gap between them is the remaining time
   * value and is the thing worth reading.
   */
  const t0Path =
    todayValues === null
      ? null
      : plot.points
          .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.spot).toFixed(1)},${y(todayValues[i]!).toFixed(1)}`)
          .join(' ')
  /* Closed back along the ZERO line, not along the bottom of the box, so the
     shaded area reads as profit-above / loss-below rather than as volume. */
  const area = `${path} L${x(plot.window.to).toFixed(1)},${zeroY.toFixed(1)} L${x(plot.window.from).toFixed(1)},${zeroY.toFixed(1)} Z`

  /*
   * Where zero sits between the top and bottom of the AXIS, so the gradient
   * turns colour exactly on the zero line.
   *
   * The gradient is in user space for this reason. `objectBoundingBox` would
   * span the drawn path's bounding box instead, which is a different box
   * whenever the curve does not reach both extremes of the axis, and the
   * colour change would then sit above or below the line it marks.
   */
  const yHi = y(hi)
  const yLo = y(lo)
  const zeroFrac = Math.min(Math.max((zeroY - yHi) / Math.max(yLo - yHi, 1), 0), 1)
  const tickCount = width < 500 ? 3 : 5
  const ticks = Array.from({ length: tickCount }, (_, i) => plot.window.from + (plot.window.to - plot.window.from) * i / (tickCount - 1))
  const scrub: PayoffScrub | null =
    scrubX === null ? null : scrubAt(plot, scrubX, W, PAD.l, PAD.r, todayValues)

  /*
   * Where the readout box sits. It follows the line but is clamped inside
   * the plot, and flips to the left once the line is past the midpoint.
   */
  const SCRUB_W = 112
  // The LINE's x, which is the snapped sample — not `scrubX`, the raw column
  // the pointer is over. They differ by up to half a sample's width.
  const lineX = scrub === null ? 0 : x(scrub.spot)
  const scrubBoxX = Math.min(
    W - PAD.r - SCRUB_W,
    Math.max(PAD.l, lineX > (PAD.l + W - PAD.r) / 2 ? lineX - SCRUB_W - 8 : lineX + 8),
  )

  // Keep labels legible when two breakevens are only a few pixels apart.
  const labelledBreakevens = plot.breakevens.filter((b, i, all) => i === 0 || x(b) - x(all[i - 1]!) > 80)

  return (
    <div ref={container} className={cn('flex min-h-0 min-w-0 flex-col', className)} data-testid="payoff-chart">
      {/* `height` is both the drawing's coordinate space and its real
          height, so nothing is scaled vertically and the labels stay
          round. A caller that owns the space measures it and passes it. */}
      <svg
        viewBox={`0 0 ${W} ${height}`}
        height={height}
        className="w-full shrink-0"
        preserveAspectRatio="xMidYMid meet"
        data-testid="payoff-svg"
        role="img"
        aria-label="Payoff at expiry">
        <defs>
          <linearGradient id={`${gid}f`} gradientUnits="userSpaceOnUse" x1="0" y1={yHi} x2="0" y2={yLo}>
            <stop offset="0" stopColor="hsl(var(--success))" stopOpacity="0.3" />
            <stop offset={zeroFrac} stopColor="hsl(var(--success))" stopOpacity="0.02" />
            <stop offset={zeroFrac} stopColor="hsl(var(--destructive))" stopOpacity="0.02" />
            <stop offset="1" stopColor="hsl(var(--destructive))" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id={`${gid}s`} gradientUnits="userSpaceOnUse" x1="0" y1={yHi} x2="0" y2={yLo}>
            <stop offset="0" stopColor="hsl(var(--success))" />
            <stop offset={zeroFrac} stopColor="hsl(var(--success))" />
            <stop offset={zeroFrac} stopColor="hsl(var(--destructive))" />
            <stop offset="1" stopColor="hsl(var(--destructive))" />
          </linearGradient>
        </defs>

        {[hi, (hi + lo) / 2, lo].map((value, i) => (
          <g key={i}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(value)} y2={y(value)} stroke="hsl(var(--border))" strokeWidth="0.7" strokeDasharray="3 5" />
            <text x={PAD.l - 12} y={y(value) + 4} fontSize="12" fontWeight="500" textAnchor="end" fill="hsl(var(--muted-foreground))" className="num">{value === 0 ? '0' : (value < 0 ? '-₹' : '₹') + compactInr(Math.abs(value))}</text>
          </g>
        ))}
        <line x1={PAD.l} x2={W - PAD.r} y1={zeroY} y2={zeroY} stroke="hsl(var(--muted-foreground))" strokeOpacity="0.4" strokeWidth="1" />
        <path d={area} fill={`url(#${gid}f)`} />
        <path d={path} fill="none" stroke={`url(#${gid}s)`} strokeWidth="2.2" strokeLinejoin="round" />
        {t0Path !== null && (
          <path
            d={t0Path}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="1.2"
            strokeDasharray="5 4"
            opacity="0.75"
            data-testid="payoff-t0"
          />
        )}

        {/* Every strike, so the structure's corners are locatable. */}
        {[...new Set(legs.map((l) => l.strike).filter((s): s is number => s !== null))].map((s) => (
          <line key={s} x1={x(s)} x2={x(s)} y1={PAD.t} y2={height - PAD.b}
            stroke="hsl(var(--border))" strokeDasharray="2 6" strokeWidth="0.8" />
        ))}

        {plot.breakevens.map((b) => (
          <g key={b}>
            <circle cx={x(b)} cy={zeroY} r="3" fill="hsl(var(--background))"
              stroke="hsl(var(--foreground))" strokeWidth="1.3" />
            {labelledBreakevens.includes(b) && <text x={Math.min(W - PAD.r - 30, Math.max(PAD.l + 30, x(b)))} y={Math.max(PAD.t + 14, zeroY - 10)} fontSize="12" fontWeight="500" textAnchor="middle"
              fill="hsl(var(--foreground))" className="num">
              {Math.round(b).toLocaleString('en-IN')}
            </text>}
          </g>
        ))}

        {spot !== null && spot > 0 && plot.atSpot !== null && (
          <g data-testid="payoff-spot">
            <line x1={x(spot)} x2={x(spot)} y1={PAD.t} y2={height - PAD.b}
              stroke="hsl(var(--warning))" strokeWidth="1" strokeDasharray="4 3" />
            {/* Where the position actually stands, which is ON the curve. */}
            <circle cx={x(spot)} cy={y(plot.atSpot)} r="3.5" fill="hsl(var(--warning))" />
            <rect x={Math.min(W - PAD.r - 96, Math.max(PAD.l, x(spot) - 48))} y={4} width={96} height={21} rx={4} fill="hsl(var(--card))" stroke="hsl(var(--warning) / 0.45)" />
            <text x={Math.min(W - PAD.r - 48, Math.max(PAD.l + 48, x(spot)))} y={18} fontSize="12" fontWeight="500" textAnchor="middle"
              fill="hsl(var(--warning))" className="num" data-testid="payoff-spot-label">
              Spot {Math.round(spot).toLocaleString('en-IN')}
            </text>
          </g>
        )}

        {/*
          ── THE READOUT — 9.47 ──────────────────────────────────────────
          Drawn after the curves and before the axis, so the line sits over
          the plot and under nothing the reader needs. The dot is placed at
          the SAMPLED point, which is why `scrubAt` snaps: a dot a pixel off
          the curve reads as a drawing fault on the one feature whose whole
          job is to be precise.
        */}
        {scrub !== null && (
          <g data-testid="payoff-scrub" pointerEvents="none">
            <line x1={x(scrub.spot)} x2={x(scrub.spot)} y1={PAD.t} y2={height - PAD.b}
              stroke="hsl(var(--foreground))" strokeOpacity="0.35" strokeWidth="1" />
            <circle cx={x(scrub.spot)} cy={y(scrub.expiry)} r="3.5"
              fill="hsl(var(--background))" stroke="hsl(var(--success))" strokeWidth="2" />
            {scrub.today !== null && (
              <circle cx={x(scrub.spot)} cy={y(scrub.today)} r="3"
                fill="hsl(var(--background))" stroke="hsl(var(--primary))" strokeWidth="1.6" />
            )}
            {/*
              The box is clamped inside the plot and FLIPS to the left of
              the line once the line is past the midpoint, so it never
              leaves the frame and never covers the curve it is reading.
            */}
            <g transform={`translate(${scrubBoxX}, ${PAD.t + 4})`}>
              <rect width={SCRUB_W} height={scrub.today === null ? 34 : 48} rx="5"
                fill="hsl(var(--card))" stroke="hsl(var(--border))" />
              <text x="8" y="14" fontSize="10" fontWeight="600" fill="hsl(var(--muted-foreground))"
                className="num" data-testid="payoff-scrub-spot">
                {Math.round(scrub.spot).toLocaleString('en-IN')}
              </text>
              <text x="8" y="28" fontSize="11" fontWeight="600" className="num"
                fill={scrub.expiry < 0 ? 'hsl(var(--destructive))' : 'hsl(var(--success))'}
                data-testid="payoff-scrub-expiry">
                {signedInr(scrub.expiry)} at expiry
              </text>
              {scrub.today !== null && (
                <text x="8" y="42" fontSize="11" fontWeight="600" className="num"
                  fill="hsl(var(--primary))" data-testid="payoff-scrub-today">
                  {signedInr(scrub.today)} today
                </text>
              )}
            </g>
          </g>
        )}

        <line x1={PAD.l} x2={W - PAD.r} y1={height - PAD.b} y2={height - PAD.b} stroke="hsl(var(--border))" />
        {ticks.map((at, i) => (
          <text key={i} x={x(at)} y={height - 12} fontSize="12" fontWeight="500" textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'} fill="hsl(var(--muted-foreground))" className="num">
            {Math.round(at).toLocaleString('en-IN')}
          </text>
        ))}

        {/*
          ONE transparent rect takes every pointer event, rather than the
          paths. A 2.2px stroke is not a hit target, and putting handlers on
          each curve would make the readout appear and vanish as the cursor
          crossed between them.
        */}
        <rect
          x={PAD.l}
          y={PAD.t}
          width={Math.max(0, W - PAD.l - PAD.r)}
          height={Math.max(0, height - PAD.t - PAD.b)}
          fill="transparent"
          data-testid="payoff-scrub-surface"
          onPointerMove={(e) => {
            const box = e.currentTarget.ownerSVGElement?.getBoundingClientRect()
            if (!box || box.width === 0) return
            // Client pixels -> viewBox units. The SVG is width-responsive,
            // so the two scales differ on every viewport but one.
            const px = ((e.clientX - box.left) / box.width) * W
            setScrubX(px)
          }}
          onPointerLeave={() => setScrubX(null)}
        />
      </svg>

      {compact ? null : (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 pt-1 text-[10px] text-muted-foreground">
        <span data-testid="payoff-maxprofit">
          Max profit{' '}
          <b className="num text-success">
            {shape.maxProfit.kind === 'UNBOUNDED' ? 'unbounded' : compactInr(shape.maxProfit.value)}
          </b>
        </span>
        <span data-testid="payoff-maxloss">
          Max loss{' '}
          <b className="num text-destructive">
            {shape.maxLoss.kind === 'UNBOUNDED'
              ? 'unbounded'
              : compactInr(Math.abs(shape.maxLoss.value))}
          </b>
          {/* Spot has a floor and no ceiling, so a short put's worst case is
              a real number at spot zero rather than "unlimited". Saying where
              it happens stops that reading as a near-term risk. */}
          {shape.maxLoss.kind === 'FINITE' && shape.maxLoss.atSpot === 0 && (
            <span className="opacity-60"> at spot 0</span>
          )}
        </span>
        {shape.breakevens.length > 0 && (
          <span>
            Breakeven{shape.breakevens.length > 1 ? 's' : ''}{' '}
            <b className="num text-foreground">
              {shape.breakevens.map((b) => Math.round(b).toLocaleString('en-IN')).join(' · ')}
            </b>
          </span>
        )}
        <span className="ml-auto opacity-70">
          {t0Path === null ? 'At expiry' : 'Solid: at expiry · dashed: today'}
        </span>
      </div>
      )}
    </div>
  )
}
